// ---------------------------------------------------------------------------
// Generation finalization-failure copy (G1 follow-up — honest labeling of a
// browser-side failure that happened AFTER the durable write succeeded).
//
// The Cloudflare generation workflow now persists a project's blocks to
// Supabase itself and flips `settings.generation.state` to 'complete'. That
// closed the data-loss bug the orphan surface exists for (see
// generation-orphan-copy.ts) — but it opened a narrower gap: if the
// browser-side generation flow then fails BEFORE it acknowledges that
// persistence (the execute call reports a failure, or returns a status
// other than 'complete') and cleanup preserves the project, the marker
// still reads `state: 'complete'` and additionally carries `finalization:
// { state: 'failed', ... }` (see `withFailedGenerationFinalization` /
// `hasFailedGenerationFinalization` in project-generation-durability.ts).
// That window is exactly that narrow: everything after acknowledgment —
// including the redirect — rethrows without going through cleanup and is
// therefore never recorded, and a user-initiated cancellation is
// deliberately excluded (the user cancelled; the browser did not fail).
// That marker is NOT an orphan — the app generated, its blocks are saved,
// and the project opens normally — so it must never be rendered with the
// orphan's "didn't finish" wording. It also must not promise to resume or
// continue generating: no resume engine exists, and nothing here needs one,
// because there is nothing left to finish server-side.
//
// This copy must convey, in every locale: the generated blocks were saved,
// but the browser's final assembly step did not complete, so pages may open
// EMPTY; open it to see what came through, and regenerate what is missing.
// Same Record<LocaleCode, …> + resolver pattern as generation-orphan-copy.ts.
//
// It must NOT claim the app "finished generating and everything is saved"
// (the pre-2026-07-30 wording). The failure that produces this marker aborts
// before the browser writes the assembled manifest, so `manifest.pages[].
// blocks` can legitimately be empty and the preview blank. Telling the user
// everything is fine reports a blank app as a success — exactly the failure
// mode the marker exists to surface.
//
// Color choice (app-card.tsx render surface): this card uses `sky-*`
// (informational — "worth a look"), while the orphan surface next to it
// uses `amber-*` (alarm — "action needed, something is missing"). That is
// a deliberate application of an existing codebase convention, not a color
// picked ad hoc for this feature — see the same sky=info / amber=warning
// split in kg-validate-tab.tsx:35, block-validation-inspector.tsx:54, and
// application-ontology-report-surface.tsx:131,142.
// ---------------------------------------------------------------------------

import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface GenerationFinalizationCopy {
  /** Headline label — reassuring, not alarming; never "didn't finish". */
  label: string;
  /** One-line explanation: generated + saved; only the browser-side step
   * that follows did not complete. */
  detail: string;
  /** CTA that routes into the existing open flow — a nudge to look, not a
   * resume/continue-generating promise (no resume engine exists). */
  openAction: string;
}

const COPY: Record<LocaleCode, GenerationFinalizationCopy> = {
  en: {
    label: "Saved — needs a check",
    detail:
      "The generated blocks are saved, but the final assembly step in your browser didn't complete, so some pages may open empty. Open it to see what came through; anything missing can be generated again.",
    openAction: "Open and check",
  },
  ko: {
    label: "저장됨 — 확인이 필요해요",
    detail:
      "생성된 블록은 저장됐지만 브라우저에서 마지막 조립 단계를 마치지 못해, 일부 화면이 비어 있을 수 있어요. 열어서 어디까지 반영됐는지 확인하고, 빠진 부분은 다시 생성하면 됩니다.",
    openAction: "열어서 확인하기",
  },
  ja: {
    label: "保存済み — 確認が必要です",
    detail:
      "生成されたブロックは保存されていますが、ブラウザ側の最終組み立て処理が完了しなかったため、一部の画面が空のままかもしれません。開いてどこまで反映されたか確認し、足りない部分は生成し直してください。",
    openAction: "開いて確認する",
  },
  zh: {
    label: "已保存 — 需要确认",
    detail:
      "生成的区块已保存，但浏览器端的最后组装步骤没有完成，部分页面可能是空的。打开看看实际生成了哪些内容，缺失的部分可以重新生成。",
    openAction: "打开确认",
  },
  "zh-TW": {
    label: "已儲存 — 需要確認",
    detail:
      "產生的區塊已儲存，但瀏覽器端的最後組裝步驟沒有完成，部分頁面可能是空的。開啟看看實際產生了哪些內容，缺少的部分可以重新產生。",
    openAction: "開啟確認",
  },
};

export function getGenerationFinalizationCopy(
  locale: LocaleCode,
): GenerationFinalizationCopy {
  return COPY[locale] ?? COPY.en;
}
